import {
  html,
  type ReactiveController,
  type ReactiveControllerHost,
  type TemplateResult,
} from "lit";
import { cn } from "@/utilities/css";
import { OPTIONS, type FeedbackWidgetValue, type Option } from "../types";

interface FeedbackTypeChoiceCallbacks {
  onCancel: (e: Event) => void;
  onNext: () => void;
  onSelect: (value: FeedbackWidgetValue) => void;
}

export class FeedbackTypeChoiceController implements ReactiveController {
  constructor(
    private host: ReactiveControllerHost & HTMLElement,
    private callbacks: FeedbackTypeChoiceCallbacks
  ) {
    host.addController(this);
  }

  private _onKeydown(e: KeyboardEvent, value: FeedbackWidgetValue) {
    const options = Array.from(
      this.host.shadowRoot!.querySelectorAll<HTMLElement>(".eko-feedback-widget__option")
    );
    const index = OPTIONS.findIndex((opt) => opt.value === value);

    switch (e.key) {
      case "ArrowDown":
      case "ArrowRight": {
        e.preventDefault();
        const nextIndex = (index + 1) % options.length;
        this.callbacks.onSelect(OPTIONS[nextIndex].value);
        this.host.updateComplete.then(() => options[nextIndex].focus());
        break;
      }
      case "ArrowUp":
      case "ArrowLeft": {
        e.preventDefault();
        const prevIndex = (index - 1 + options.length) % options.length;
        this.callbacks.onSelect(OPTIONS[prevIndex].value);
        this.host.updateComplete.then(() => options[prevIndex].focus());
        break;
      }
      case " ":
      case "Enter":
        e.preventDefault();
        this.callbacks.onSelect(value);
        break;
    }
  }

  private _renderOption(opt: Option, value: FeedbackWidgetValue | undefined): TemplateResult {
    const checked = value === opt.value;
    const tabindex = checked || (!value && opt.value === "bug") ? 0 : -1;

    return html`
      <div
        class=${cn("eko-feedback-widget__option", {
          "eko-feedback-widget__option--checked": checked,
        })}
        role="radio"
        aria-checked=${String(checked)}
        tabindex=${tabindex}
        @click=${() => this.callbacks.onSelect(opt.value)}
        @keydown=${(e: KeyboardEvent) => this._onKeydown(e, opt.value)}>
        <div class="eko-feedback-widget__option-content">
          <eko-featured-icon
            size="sm"
            variant="square-light"
            theme="accent"
            icon=${opt.icon}></eko-featured-icon>
          <div class="eko-feedback-widget__option-text">
            <span class="eko-feedback-widget__option-label">${opt.label}</span>
            <span class="eko-feedback-widget__option-description">${opt.description}</span>
          </div>
        </div>
        <span class="eko-feedback-widget__option-radio" aria-hidden="true"></span>
      </div>
    `;
  }

  hostConnected() {}

  render(params: {
    heading: string;
    icon: string;
    supportingText: string;
    value: FeedbackWidgetValue | undefined;
  }): TemplateResult {
    const { heading, icon, supportingText, value } = params;
    return html`
      <eko-featured-icon
        slot="icon"
        variant="square-modern"
        theme="neutral"
        icon=${icon}></eko-featured-icon>
      <span slot="title" class="eko-feedback-widget__title">${heading}</span>
      <span slot="subtitle">${supportingText}</span>

      <div class="eko-feedback-widget__options" role="radiogroup" aria-label=${heading}>
        ${OPTIONS.map((opt) => this._renderOption(opt, value))}
      </div>

      <div slot="footer" class="eko-feedback-widget__footer">
        <eko-button @click=${this.callbacks.onCancel}>Cancel</eko-button>
        <eko-button variant="primary" ?disabled=${!value} @click=${this.callbacks.onNext}>
          Next
        </eko-button>
      </div>
    `;
  }
}
